import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { todayInET } from "../lib/time";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function startingMonth() {
  // YYYY-MM in ET
  return todayInET().slice(0, 7);
}

function addMonth(ym, delta) {
  const [y, m] = ym.split("-").map(Number);
  const idx = (y * 12 + (m - 1)) + delta;
  const ny = Math.floor(idx / 12);
  const nm = (idx % 12) + 1;
  return `${ny}-${String(nm).padStart(2, "0")}`;
}

function buildGrid(ym) {
  const [y, m] = ym.split("-").map(Number);
  const firstDay = new Date(y, m - 1, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(y, m, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(`${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

/**
 * Airbnb-inspired month calendar.
 *
 * Props:
 *   yachtKey  — slug or uuid used to hit /availability-summary
 *   value     — selected date "YYYY-MM-DD" | null
 *   onChange  — (dateStr) => void
 */
export default function YachtCalendar({ yachtKey, value, onChange }) {
  const [month, setMonth] = useState(() => (value ? value.slice(0, 7) : startingMonth()));
  const [statusByDate, setStatusByDate] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const today = useMemo(() => todayInET(), []);
  const minMonth = today.slice(0, 7);

  useEffect(() => {
    let ignore = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const r = await api.get(`/yachts/${yachtKey}/availability-summary`, {
          params: { month },
        });
        if (ignore) return;
        const map = {};
        for (const d of r.data.days) map[d.date] = d.status;
        setStatusByDate(map);
      } catch (e) {
        if (!ignore) setError(formatApiErrorDetail(e));
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    if (yachtKey) load();
    return () => {
      ignore = true;
    };
  }, [yachtKey, month]);

  const cells = useMemo(() => buildGrid(month), [month]);
  const [y, m] = month.split("-").map(Number);

  const canPrev = month > minMonth;

  function statusFor(dateStr) {
    if (!dateStr) return null;
    return statusByDate[dateStr] || null;
  }

  function isSelectable(status) {
    return status === "available" || status === "partial";
  }

  return (
    <div className="w-full" data-testid="yacht-calendar">
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={() => canPrev && setMonth(addMonth(month, -1))}
          disabled={!canPrev}
          className={`p-2 rounded-full transition ${
            canPrev ? "hover:bg-hbe-soft text-hbe-primary" : "text-hbe-ink/25"
          }`}
          aria-label="Previous month"
          data-testid="cal-prev"
        >
          <ChevronLeft size={20} />
        </button>
        <div
          className="font-serif text-lg text-hbe-primary"
          data-testid="cal-month-label"
        >
          {MONTH_NAMES[m - 1]} {y}
        </div>
        <button
          type="button"
          onClick={() => setMonth(addMonth(month, 1))}
          className="p-2 rounded-full text-hbe-primary hover:bg-hbe-soft transition"
          aria-label="Next month"
          data-testid="cal-next"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-xs text-hbe-ink/55 mb-1">
        {WEEKDAYS.map((w) => (
          <div key={w} className="text-center py-1 uppercase tracking-wider">
            {w}
          </div>
        ))}
      </div>

      <div
        className="grid grid-cols-7 gap-1"
        aria-live="polite"
        aria-busy={loading}
      >
        {cells.map((dateStr, i) => {
          if (!dateStr) return <div key={i} className="h-11 md:h-12" />;
          const status = statusFor(dateStr);
          const day = Number(dateStr.slice(-2));
          const selected = value === dateStr;
          const clickable = isSelectable(status);
          const isToday = dateStr === today;

          let classes =
            "h-11 md:h-12 flex items-center justify-center rounded-md text-sm transition select-none";
          if (status === "fully_booked") {
            // fully_booked takes visual priority over "selected" so a
            // pre-selected fully-booked date reads correctly.
            classes += " text-hbe-ink/35 line-through cursor-not-allowed";
          } else if (selected) {
            classes += " bg-hbe-primary text-white font-medium";
          } else if (clickable) {
            classes += " text-hbe-ink hover:bg-hbe-soft cursor-pointer";
          } else {
            classes += " text-hbe-ink/25 cursor-not-allowed";
          }
          if (isToday && !selected && status !== "fully_booked") {
            classes += " ring-1 ring-hbe-accent/50";
          }

          return (
            <button
              key={dateStr}
              type="button"
              disabled={!clickable}
              onClick={() => clickable && onChange && onChange(dateStr)}
              className={classes}
              aria-pressed={selected}
              aria-label={`${MONTH_NAMES[m - 1]} ${day}, ${y} — ${status || "loading"}`}
              data-testid={`cal-day-${dateStr}`}
              data-status={status || "loading"}
            >
              <span className="relative">
                {day}
                {status === "partial" && !selected && (
                  <span className="absolute left-1/2 -translate-x-1/2 -bottom-2 h-1 w-1 rounded-full bg-hbe-accent" />
                )}
              </span>
            </button>
          );
        })}
      </div>

      {error && (
        <div
          className="mt-3 text-sm text-red-700"
          role="alert"
          data-testid="cal-error"
        >
          {error}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-hbe-ink/55">
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-hbe-ink/20" /> Unavailable
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-hbe-accent" /> Partial
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-hbe-primary" /> Selected
        </span>
      </div>
    </div>
  );
}
